import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Banner,
  BlockStack,
  Button,
  Checkbox,
  Divider,
  InlineGrid,
  Modal,
  Select,
  Tabs,
  Text,
  TextField,
} from '@shopify/polaris';
import MontageProfilePreview from '../montageProfiles/MontageProfilePreview';
import { loadAssetPreview } from '../../services/assetPreviewService';
import { montageProfilesService } from '../../services/montageProfilesService';

const STORY_TYPE_OPTIONS = [
  { label: 'Dynamic story', value: 'dynamic_story' },
  { label: 'Legacy static', value: 'legacy_static' },
];

const LOGO_ASSET_OPTIONS = [
  { label: 'White', value: 'snaps-stories-logo-white.png' },
  { label: 'Colored', value: 'snaps-stories-logo-color.png' },
];

const LOGO_POSITION_OPTIONS = [
  { label: 'Bottom Center', value: 'bottom_center' },
  { label: 'Bottom Left', value: 'bottom_left' },
  { label: 'Bottom Right', value: 'bottom_right' },
  { label: 'Top Center', value: 'top_center' },
  { label: 'Top Left', value: 'top_left' },
  { label: 'Top Right', value: 'top_right' },
  { label: 'Center', value: 'center' },
];

const clone = (value) => JSON.parse(JSON.stringify(value || {}));

const defaultPageNumbers = () => ({
  enabled: true,
  font_size_ratio: 0.025,
  minimum_font_size_pt: 8,
  horizontal_margin_cm: 4,
  bottom_margin_cm: 2.5,
  shadow_ratio: 0.04,
});

const defaultBookletPageNumbers = () => ({
  enabled: true,
  source: 'reading_order',
  start_at: 1,
  exclude_roles: ['cover', 'back'],
  placement: 'outer_bottom',
  font_size_pt: 10,
  bottom_margin_cm: 0.5,
  outer_margin_cm: 0.5,
});

const defaultMontage = (storyType) => ({
  variant: storyType === 'dynamic_story' ? 'no_cover' : 'with_cover',
  page: {
    content_width_cm: 20,
    content_height_cm: 20,
    margin_left_cm: 1.5,
    margin_right_cm: 1.5,
    margin_top_cm: 2.5,
    margin_bottom_cm: 2.5,
  },
  scene_selection: {
    include_cover: storyType !== 'dynamic_story',
    include_back: storyType !== 'dynamic_story',
  },
  instruction_page: {
    enabled: true,
    position: storyType === 'dynamic_story' ? 'first' : 'after_cover',
    font_size_pt: 16,
  },
  page_numbers: {
    enabled: true,
    font_size_pt: 12,
    bottom_offset_pt: -6,
    include_order_number: false,
  },
  barcode: {
    enabled: storyType !== 'dynamic_story',
    width_pt: 70,
    height_pt: 25,
    padding_pt: 5,
    bottom_offset_pt: 10,
  },
  rtl: {
    swap_interior_pairs: true,
  },
});

const defaultBooklet = () => ({
  width_cm: 41,
  height_cm: 20,
  logical_pages_per_spread: 2,
  pad_to_multiple: 4,
  image_fit: 'stretch',
  rtl_cover_swap_only: true,
  page_numbers: defaultBookletPageNumbers(),
});

const defaultReadable = () => ({
  variants: {
    '41x20': {
      width_cm: 41,
      height_cm: 20,
      include_cover: true,
      page_numbers: defaultPageNumbers(),
    },
    '41x20-nocover': {
      width_cm: 41,
      height_cm: 20,
      include_cover: false,
      page_numbers: defaultPageNumbers(),
    },
    '38x19': {
      width_cm: 38,
      height_cm: 19,
      include_cover: true,
      page_numbers: defaultPageNumbers(),
    },
  },
  reverse_output_order: true,
  image_fit: 'stretch',
});

const defaultCoverBranding = () => ({
  enabled: false,
  gradient_color: '#846dec',
  gradient_height_pct: 0.25,
  gradient_bottom_opacity: 0.82,
  logo_width_pct: 0.3,
  logo_bottom_margin_pct: 0.04,
  gradient: {
    color: '#846dec',
    opacity: 0.82,
    top_cm: 0,
    right_cm: 0,
    bottom_cm: 0,
    left_cm: 0,
  },
  logo: {
    asset: 'snaps-stories-logo-white.png',
    position: 'bottom_center',
    width_pct: 0.3,
    width_cm: 0,
    bottom_margin_cm: 0,
    horizontal_margin_cm: 0,
  },
});

const defaultBackBranding = () => ({
  enabled: true,
  gradient: {
    color: '#ffffff',
    opacity: 1,
    top_cm: 0,
    right_cm: 0,
    bottom_cm: 0,
    left_cm: 0,
  },
});

const BACK_LAYER_ASSET_OPTIONS = [
  { label: 'Stories QR card', value: 'back-cover-qr-layer.png' },
];

const defaultBackLayer = () => ({
  enabled: false,
  asset: 'back-cover-qr-layer.png',
  width_cm: 10.1,
  order_id: {
    enabled: true,
    x_pct: 0.68,
    y_pct: 0.52,
    font_size_pt: 9,
    color: '#ffffff',
  },
});

const mergeBackLayer = (saved) => {
  const defaults = defaultBackLayer();
  const layer = saved && typeof saved === 'object' ? saved : {};
  return {
    ...defaults,
    ...layer,
    order_id: { ...defaults.order_id, ...(layer.order_id || {}) },
  };
};

const defaultCover = (storyType) => (
  storyType === 'dynamic_story'
    ? {
      variant: 'dynamic_background',
      width_px: 6300,
      height_px: 3186,
      dpi: 300,
      background_fit: 'cover',
      cover_width_px: 2490,
      cover_height_px: 2490,
      inner_gap_half_px: 77.13,
      cover_fit: 'cover',
      branding: defaultCoverBranding(),
    }
    : {
      variant: 'a3_background',
      width_px: 4961,
      height_px: 3508,
      dpi: 300,
      background_fit: 'cover',
      cover_fit: 'half_width_centered',
      branding: defaultCoverBranding(),
    }
);

const emptyProfile = () => ({
  key: '',
  name: '',
  description: '',
  story_types: ['dynamic_story'],
  active: true,
  outputs: {
    montage: defaultMontage('dynamic_story'),
    booklet: defaultBooklet(),
    cover: defaultCover('dynamic_story'),
  },
});

const normalizeProfileForForm = (profile) => {
  const normalized = clone(profile || emptyProfile());
  normalized.story_types = Array.isArray(normalized.story_types) && normalized.story_types.length
    ? normalized.story_types
    : ['dynamic_story'];
  normalized.outputs = normalized.outputs || {};
  if (normalized.outputs.booklet) {
    const pageNumbers = normalized.outputs.booklet.page_numbers || {};
    normalized.outputs.booklet = {
      ...defaultBooklet(),
      ...normalized.outputs.booklet,
      logical_pages_per_spread: 2,
    };
    normalized.outputs.booklet.page_numbers = {
      ...defaultBookletPageNumbers(),
      ...pageNumbers,
      exclude_roles: Array.isArray(pageNumbers.exclude_roles)
        ? pageNumbers.exclude_roles
        : defaultBookletPageNumbers().exclude_roles,
    };
  }
  if (normalized.outputs.cover) {
    normalized.outputs.cover.back_layer = mergeBackLayer(normalized.outputs.cover.back_layer);
  }
  return normalized;
};

const setAtPath = (value, path, nextValue) => {
  const next = clone(value);
  let cursor = next;
  path.slice(0, -1).forEach((segment) => {
    cursor[segment] = cursor[segment] || {};
    cursor = cursor[segment];
  });
  cursor[path[path.length - 1]] = nextValue;
  return next;
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const buildProfilePayload = (profile) => {
  const next = clone(profile);
  delete next._id;
  delete next.revision;
  delete next.created_at;
  delete next.updated_at;

  const montage = next.outputs?.montage;
  if (montage) {
    Object.keys(montage.page || {}).forEach((key) => {
      montage.page[key] = toNumber(montage.page[key]);
    });
    ['font_size_pt', 'bottom_offset_pt'].forEach((key) => {
      if (montage.page_numbers?.[key] !== undefined) {
        montage.page_numbers[key] = toNumber(montage.page_numbers[key]);
      }
    });
    if (montage.instruction_page?.font_size_pt !== undefined) {
      montage.instruction_page.font_size_pt = toNumber(
        montage.instruction_page.font_size_pt
      );
    }
    ['width_pt', 'height_pt', 'padding_pt', 'bottom_offset_pt'].forEach((key) => {
      if (montage.barcode?.[key] !== undefined) {
        montage.barcode[key] = toNumber(montage.barcode[key]);
      }
    });
  }

  const booklet = next.outputs?.booklet;
  if (booklet) {
    // This is a fixed renderer invariant, not a user-editable value.
    booklet.logical_pages_per_spread = 2;
    [
      'width_cm',
      'height_cm',
      'pad_to_multiple',
    ].forEach((key) => {
      booklet[key] = toNumber(booklet[key]);
    });
    const pageNumbers = booklet.page_numbers || defaultBookletPageNumbers();
    ['start_at', 'font_size_pt', 'bottom_margin_cm', 'outer_margin_cm'].forEach((key) => {
      pageNumbers[key] = toNumber(pageNumbers[key]);
    });
    booklet.page_numbers = pageNumbers;
  }

  const cover = next.outputs?.cover;
  if (cover) {
    [
      'width_px',
      'height_px',
      'dpi',
      'cover_width_px',
      'cover_height_px',
      'inner_gap_half_px',
      'cover_width_cm',
      'cover_height_cm',
      'inner_gap_cm',
      'outer_margin_cm',
    ].forEach((key) => {
      if (cover[key] !== undefined) cover[key] = toNumber(cover[key]);
    });
    cover.branding = { ...defaultCoverBranding(), ...(cover.branding || {}) };
    ['gradient_height_pct', 'gradient_bottom_opacity', 'logo_width_pct', 'logo_bottom_margin_pct']
      .forEach((key) => {
        if (cover.branding[key] !== undefined) cover.branding[key] = toNumber(cover.branding[key]);
      });
    if (cover.branding.gradient) {
      ['top_cm', 'right_cm', 'bottom_cm', 'left_cm', 'opacity'].forEach((key) => {
        if (cover.branding.gradient[key] !== undefined) {
          cover.branding.gradient[key] = toNumber(cover.branding.gradient[key]);
        }
      });
    }
    if (cover.branding.logo) {
      ['width_pct', 'width_cm', 'bottom_margin_cm', 'horizontal_margin_cm'].forEach((key) => {
        if (cover.branding.logo[key] !== undefined) {
          cover.branding.logo[key] = toNumber(cover.branding.logo[key]);
        }
      });
    }
    if (cover.back_branding?.gradient) {
      ['top_cm', 'right_cm', 'bottom_cm', 'left_cm', 'opacity'].forEach((key) => {
        if (cover.back_branding.gradient[key] !== undefined) {
          cover.back_branding.gradient[key] = toNumber(cover.back_branding.gradient[key]);
        }
      });
    }
    if (cover.back_layer) {
      cover.back_layer.width_cm = toNumber(cover.back_layer.width_cm);
      ['x_pct', 'y_pct', 'font_size_pt'].forEach((key) => {
        if (cover.back_layer.order_id?.[key] !== undefined) {
          cover.back_layer.order_id[key] = toNumber(cover.back_layer.order_id[key]);
        }
      });
    }
  }

  Object.values(next.outputs?.readable?.variants || {}).forEach((variant) => {
    variant.width_cm = toNumber(variant.width_cm);
    variant.height_cm = toNumber(variant.height_cm);
    [
      'scene_width_cm',
      'scene_height_cm',
      'outer_margin_cm',
      'top_margin_cm',
    ].forEach((key) => {
      if (variant[key] !== undefined) variant[key] = toNumber(variant[key]);
    });
    const pageNumbers = variant.page_numbers || {};
    [
      'font_size_ratio',
      'minimum_font_size_pt',
      'horizontal_margin_cm',
      'bottom_margin_cm',
      'shadow_ratio',
    ].forEach((key) => {
      pageNumbers[key] = toNumber(pageNumbers[key]);
    });
  });

  return next;
};

const NumberField = ({ label, value, onChange, suffix, helpText, disabled = false }) => (
  <TextField
    label={label}
    type="number"
    value={String(value ?? '')}
    onChange={onChange}
    suffix={suffix}
    helpText={helpText}
    disabled={disabled}
    autoComplete="off"
  />
);

const Section = ({ title, children }) => (
  <div style={{ padding: 16, border: '1px solid #e1e3e5', borderRadius: 8 }}>
    <BlockStack gap="300">
      <Text variant="headingMd" as="h3">{title}</Text>
      {children}
    </BlockStack>
  </div>
);

const MontageProfileModal = ({
  open,
  mode,
  profile,
  profiles,
  onClose,
  onSave,
  saving,
}) => {
  const [form, setForm] = useState(emptyProfile());
  const [baseProfileKey, setBaseProfileKey] = useState('');
  const [error, setError] = useState('');
  const [previewLane, setPreviewLane] = useState('hard_cover');
  const [previewDirection, setPreviewDirection] = useState('ltr');
  const [previewGenerationId, setPreviewGenerationId] = useState('');
  const [previewSource, setPreviewSource] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [exactPreviewStatus, setExactPreviewStatus] = useState('');
  const [exactPreviewUrls, setExactPreviewUrls] = useState({});
  const initializedModalKey = useRef('');
  const isCreate = mode === 'create';
  const storyType = form.story_types?.[0] || 'dynamic_story';

  useEffect(() => {
    if (!open) {
      initializedModalKey.current = '';
      return;
    }
    const modalKey = `${mode}:${profile?.key || 'new'}`;
    if (initializedModalKey.current === modalKey) return;
    const initial = normalizeProfileForForm(profile || profiles?.[0] || emptyProfile());
    if (isCreate) {
      initial.key = '';
      initial.name = '';
      initial.description = '';
      initial.active = true;
      setBaseProfileKey(profile?.key || profiles?.[0]?.key || '');
    }
    setForm(initial);
    setError('');
    setPreviewSource(null);
    setPreviewGenerationId('');
    setPreviewImages([]);
    setPreviewError('');
    setExactPreviewStatus('');
    setExactPreviewUrls({});
    initializedModalKey.current = modalKey;
  }, [open, profile, profiles, isCreate]);

  useEffect(() => () => {
    previewImages.forEach((url) => URL.revokeObjectURL(url));
  }, [previewImages]);

  useEffect(() => () => {
    Object.values(exactPreviewUrls).forEach((url) => URL.revokeObjectURL(url));
  }, [exactPreviewUrls]);

  const baseOptions = useMemo(() => (
    (profiles || [])
      .filter((item) => item.active !== false)
      .map((item) => ({
        label: `${item.name} (${item.key})`,
        value: item.key,
      }))
  ), [profiles]);

  const update = (path, value) => setForm((current) => setAtPath(current, path, value));

  const changeBaseProfile = (key) => {
    const base = profiles.find((item) => item.key === key);
    if (!base) return;
    const next = normalizeProfileForForm(base);
    next.key = form.key;
    next.name = form.name;
    next.description = form.description;
    next.active = true;
    setBaseProfileKey(key);
    setForm(next);
  };

  const changeStoryType = (nextStoryType) => {
    setForm((current) => {
      const next = clone(current);
      next.story_types = [nextStoryType];
      if (next.outputs.montage) next.outputs.montage = defaultMontage(nextStoryType);
      if (next.outputs.cover) next.outputs.cover = defaultCover(nextStoryType);
      return next;
    });
  };

  const toggleOutput = (output, enabled) => {
    setForm((current) => {
      const next = clone(current);
      if (!enabled) {
        delete next.outputs[output];
        return next;
      }
      if (output === 'montage') next.outputs.montage = defaultMontage(storyType);
      if (output === 'booklet') next.outputs.booklet = defaultBooklet();
      if (output === 'readable') next.outputs.readable = defaultReadable();
      if (output === 'cover') next.outputs.cover = defaultCover(storyType);
      return next;
    });
  };

  const handleSave = async () => {
    const key = String(form.key || '').trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9_-]{1,79}$/.test(key)) {
      setError('Key must contain 2–80 lowercase letters, numbers, hyphens, or underscores.');
      return;
    }
    if (!String(form.name || '').trim()) {
      setError('Name is required.');
      return;
    }
    if (!form.outputs || Object.keys(form.outputs).length === 0) {
      setError('Enable at least one output.');
      return;
    }

    try {
      setError('');
      await onSave(buildProfilePayload({
        ...form,
        key,
        name: form.name.trim(),
        description: String(form.description || '').trim(),
      }));
    } catch (saveError) {
      setError(saveError.message || 'Failed to save montage profile.');
    }
  };

  const clearPreviewUrls = () => {
    Object.values(exactPreviewUrls).forEach((url) => URL.revokeObjectURL(url));
    setExactPreviewUrls({});
  };

  const loadGenerationPreview = async () => {
    const generationId = previewGenerationId.trim();
    if (!generationId) {
      setPreviewError('Enter a generation ID.');
      return;
    }
    setPreviewLoading(true);
    setPreviewError('');
    clearPreviewUrls();
    try {
      const source = await montageProfilesService.getPreviewSource(generationId);
      const loaded = await Promise.all(
        (source.scenes || []).map(async (scene) => {
          try {
            return URL.createObjectURL(await loadAssetPreview(scene.image_url));
          } catch (_error) {
            return null;
          }
        })
      );
      previewImages.forEach((url) => URL.revokeObjectURL(url));
      setPreviewImages(loaded.filter(Boolean));
      setPreviewSource(source);
      setPreviewDirection(source.direction || 'ltr');
    } catch (loadError) {
      setPreviewError(loadError.message || 'Failed to load generation scenes.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderExactPreview = async () => {
    const generationId = previewGenerationId.trim();
    if (!generationId) {
      setPreviewError('Load an existing generation before rendering an exact preview.');
      return;
    }
    setPreviewLoading(true);
    setPreviewError('');
    clearPreviewUrls();
    try {
      const previewProfile = buildProfilePayload({
        ...form,
        key: String(form.key || 'unsaved-preview').trim().toLowerCase(),
        name: String(form.name || 'Unsaved preview').trim(),
        revision: profile?.revision || 1,
      });
      let job = await montageProfilesService.createExactPreview(generationId, {
        profile: previewProfile,
        lane: previewLane,
        direction: previewDirection,
      });
      setExactPreviewStatus(job.status);
      for (let attempt = 0; attempt < 90 && job.status !== 'completed'; attempt += 1) {
        if (job.status === 'failed') {
          throw new Error(job.error || 'Exact montage preview failed.');
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
        job = await montageProfilesService.getExactPreview(generationId, job.job_id);
        setExactPreviewStatus(job.status);
      }
      if (job.status !== 'completed' || !job.result) {
        throw new Error('Exact montage preview timed out.');
      }
      const artifacts = {};
      for (const artifact of ['cover', 'inner']) {
        if (!job.result.artifacts?.[artifact]) continue;
        const blob = await montageProfilesService.getExactPreviewArtifact(
          generationId,
          job.job_id,
          artifact
        );
        artifacts[artifact] = URL.createObjectURL(blob);
      }
      setExactPreviewUrls(artifacts);
    } catch (renderError) {
      setExactPreviewStatus('failed');
      setPreviewError(renderError.message || 'Failed to render exact preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const montage = form.outputs?.montage;
  const booklet = form.outputs?.booklet;
  const readable = form.outputs?.readable;
  const cover = form.outputs?.cover;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isCreate ? 'Add montage profile' : `Edit ${profile?.name || 'montage profile'}`}
      large
      primaryAction={{
        content: isCreate ? 'Create profile' : 'Save profile',
        onAction: handleSave,
        loading: saving,
      }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {error && <Banner tone="critical">{error}</Banner>}

          {isCreate && baseOptions.length > 0 && (
            <Select
              label="Start from existing profile"
              options={baseOptions}
              value={baseProfileKey}
              onChange={changeBaseProfile}
              helpText="Copies all geometry and output rules, then saves them under a new key."
            />
          )}

          <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
            <TextField
              label="Name"
              value={form.name || ''}
              onChange={(value) => update(['name'], value)}
              autoComplete="off"
            />
            <TextField
              label="Key"
              value={form.key || ''}
              onChange={(value) => update(['key'], value.toLowerCase().replace(/\s+/g, '-'))}
              disabled={!isCreate}
              autoComplete="off"
            />
          </InlineGrid>

          <TextField
            label="Description"
            value={form.description || ''}
            onChange={(value) => update(['description'], value)}
            multiline={2}
            autoComplete="off"
          />

          <Select
            label="Compatible story type"
            options={STORY_TYPE_OPTIONS}
            value={storyType}
            onChange={changeStoryType}
          />

          <Divider />
          <Text variant="headingMd" as="h2">Outputs</Text>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
            {['montage', 'booklet', 'readable', 'cover'].map((output) => (
              <Checkbox
                key={output}
                label={output.charAt(0).toUpperCase() + output.slice(1)}
                checked={Boolean(form.outputs?.[output])}
                onChange={(checked) => toggleOutput(output, checked)}
              />
            ))}
          </InlineGrid>

          <Section title="Live montage preview">
            <Tabs
              tabs={[
                { id: 'hard_cover', content: 'Hard cover' },
                { id: 'booklet', content: 'Booklet' },
              ]}
              selected={['hard_cover', 'booklet'].indexOf(previewLane)}
              onSelect={(index) => {
                setPreviewLane(['hard_cover', 'booklet'][index]);
                clearPreviewUrls();
                setExactPreviewStatus('');
              }}
            />
            <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
              <Select
                label="Reading direction"
                options={[
                  { label: 'Left to right', value: 'ltr' },
                  { label: 'Right to left (Arabic)', value: 'rtl' },
                ]}
                value={previewDirection}
                onChange={(value) => {
                  setPreviewDirection(value);
                  clearPreviewUrls();
                }}
              />
              <TextField
                label="Existing generation ID"
                value={previewGenerationId}
                onChange={setPreviewGenerationId}
                placeholder="Optional, for realistic scenes and exact rendering"
                autoComplete="off"
                connectedRight={(
                  <Button onClick={loadGenerationPreview} loading={previewLoading}>
                    Load
                  </Button>
                )}
              />
            </InlineGrid>
            {previewError && <Banner tone="critical">{previewError}</Banner>}
            {previewSource && (
              <Banner tone="info">
                {`Loaded ${previewSource.scenes?.length || 0} scenes from ${
                  previewSource.generation_id
                } (${previewSource.story_slug}).`}
              </Banner>
            )}
            <MontageProfilePreview
              profile={form}
              lane={previewLane}
              direction={previewDirection}
              sceneImages={previewImages}
            />
            <Button
              variant="primary"
              onClick={renderExactPreview}
              loading={previewLoading}
              disabled={!previewGenerationId.trim()}
            >
              Render exact Python preview
            </Button>
            {exactPreviewStatus && (
              <Text variant="bodySm" tone="subdued">
                {`Exact preview status: ${exactPreviewStatus}`}
              </Text>
            )}
            {Object.entries(exactPreviewUrls).map(([artifact, url]) => (
              <BlockStack gap="150" key={artifact}>
                <Text fontWeight="semibold">
                  {artifact === 'cover' ? 'Exact cover PDF' : 'Exact inner PDF'}
                </Text>
                <iframe
                  title={`Exact ${artifact} montage preview`}
                  src={url}
                  style={{ width: '100%', height: 420, border: '1px solid #e1e3e5' }}
                />
              </BlockStack>
            ))}
          </Section>

          {montage && (
            <Section title="Montage pages">
              <Select
                label="Cover policy"
                options={storyType === 'dynamic_story'
                  ? [{ label: 'Interior pages only', value: 'no_cover' }]
                  : [{ label: 'Include front and back cover', value: 'with_cover' }]}
                value={montage.variant}
                onChange={(value) => update(['outputs', 'montage', 'variant'], value)}
                disabled
                helpText="The montage cover policy is fixed by the compatible story type."
              />
              <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
                {[
                  ['Content width', 'content_width_cm'],
                  ['Content height', 'content_height_cm'],
                  ['Left margin', 'margin_left_cm'],
                  ['Right margin', 'margin_right_cm'],
                  ['Top margin', 'margin_top_cm'],
                  ['Bottom margin', 'margin_bottom_cm'],
                ].map(([label, key]) => (
                  <NumberField
                    key={key}
                    label={label}
                    value={montage.page?.[key]}
                    suffix="cm"
                    onChange={(value) => update(['outputs', 'montage', 'page', key], value)}
                  />
                ))}
              </InlineGrid>
              <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
                <NumberField
                  label="Instruction font"
                  value={montage.instruction_page?.font_size_pt}
                  suffix="pt"
                  onChange={(value) => update(
                    ['outputs', 'montage', 'instruction_page', 'font_size_pt'],
                    value
                  )}
                />
                <NumberField
                  label="Page number font"
                  value={montage.page_numbers?.font_size_pt}
                  suffix="pt"
                  onChange={(value) => update(
                    ['outputs', 'montage', 'page_numbers', 'font_size_pt'],
                    value
                  )}
                />
                <NumberField
                  label="Page number vertical offset"
                  value={montage.page_numbers?.bottom_offset_pt}
                  suffix="pt"
                  onChange={(value) => update(
                    ['outputs', 'montage', 'page_numbers', 'bottom_offset_pt'],
                    value
                  )}
                />
              </InlineGrid>
              <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                <Checkbox
                  label="Instruction page"
                  checked={montage.instruction_page?.enabled !== false}
                  onChange={(checked) => update(
                    ['outputs', 'montage', 'instruction_page', 'enabled'],
                    checked
                  )}
                />
                <Checkbox
                  label="Page numbers"
                  checked={montage.page_numbers?.enabled !== false}
                  onChange={(checked) => update(
                    ['outputs', 'montage', 'page_numbers', 'enabled'],
                    checked
                  )}
                />
                <Checkbox
                  label="Include order number"
                  checked={montage.page_numbers?.include_order_number === true}
                  disabled={montage.page_numbers?.enabled === false}
                  onChange={(checked) => update(
                    ['outputs', 'montage', 'page_numbers', 'include_order_number'],
                    checked
                  )}
                  helpText="Adds order ID beside page numbers (e.g. #1004 - 1)"
                />
                <Checkbox
                  label="Barcode"
                  checked={montage.barcode?.enabled === true}
                  onChange={(checked) => update(
                    ['outputs', 'montage', 'barcode', 'enabled'],
                    checked
                  )}
                  disabled={storyType === 'dynamic_story'}
                  helpText={storyType === 'dynamic_story'
                    ? 'Dynamic no-cover montage PDFs do not include a barcode.'
                    : undefined}
                />
              </InlineGrid>
            </Section>
          )}

          {booklet && (
            <Section title="Booklet imposition">
              <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                <NumberField
                  label="Spread width"
                  value={booklet.width_cm}
                  suffix="cm"
                  onChange={(value) => update(['outputs', 'booklet', 'width_cm'], value)}
                />
                <NumberField
                  label="Spread height"
                  value={booklet.height_cm}
                  suffix="cm"
                  onChange={(value) => update(['outputs', 'booklet', 'height_cm'], value)}
                />
                <NumberField
                  label="Pages per spread"
                  value={booklet.logical_pages_per_spread}
                  onChange={(value) => update(
                    ['outputs', 'booklet', 'logical_pages_per_spread'],
                    value
                  )}
                  helpText="Currently fixed at two logical pages."
                  disabled
                />
                <NumberField
                  label="Pad page count to"
                  value={booklet.pad_to_multiple}
                  onChange={(value) => update(
                    ['outputs', 'booklet', 'pad_to_multiple'],
                    value
                  )}
                />
              </InlineGrid>
              <Select
                label="Image fit"
                options={[{ label: 'Stretch to page', value: 'stretch' }]}
                value={booklet.image_fit || 'stretch'}
                onChange={(value) => update(['outputs', 'booklet', 'image_fit'], value)}
                disabled
                helpText="Booklet imposition currently stretches each prepared spread to the page."
              />
              <Divider />
              <Text variant="headingSm" as="h3">Page numbers</Text>
              <Checkbox
                label="Enable booklet page numbers"
                checked={booklet.page_numbers?.enabled !== false}
                onChange={(checked) => update(
                  ['outputs', 'booklet', 'page_numbers', 'enabled'],
                  checked
                )}
              />
              <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                <NumberField
                  label="Starting number"
                  value={booklet.page_numbers?.start_at}
                  onChange={(value) => update(
                    ['outputs', 'booklet', 'page_numbers', 'start_at'],
                    value
                  )}
                  disabled={booklet.page_numbers?.enabled === false}
                />
                <NumberField
                  label="Font size"
                  value={booklet.page_numbers?.font_size_pt}
                  suffix="pt"
                  onChange={(value) => update(
                    ['outputs', 'booklet', 'page_numbers', 'font_size_pt'],
                    value
                  )}
                  disabled={booklet.page_numbers?.enabled === false}
                />
                <NumberField
                  label="Outer margin"
                  value={booklet.page_numbers?.outer_margin_cm}
                  suffix="cm"
                  onChange={(value) => update(
                    ['outputs', 'booklet', 'page_numbers', 'outer_margin_cm'],
                    value
                  )}
                  disabled={booklet.page_numbers?.enabled === false}
                  helpText="Distance from the outside edge of each imposed spread."
                />
                <NumberField
                  label="Bottom margin"
                  value={booklet.page_numbers?.bottom_margin_cm}
                  suffix="cm"
                  onChange={(value) => update(
                    ['outputs', 'booklet', 'page_numbers', 'bottom_margin_cm'],
                    value
                  )}
                  disabled={booklet.page_numbers?.enabled === false}
                />
              </InlineGrid>
              <Select
                label="Placement"
                options={[{ label: 'Outer bottom', value: 'outer_bottom' }]}
                value={booklet.page_numbers?.placement || 'outer_bottom'}
                onChange={(value) => update(
                  ['outputs', 'booklet', 'page_numbers', 'placement'],
                  value
                )}
                disabled
                helpText="The booklet renderer currently supports outer-bottom placement."
              />
              <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                {['cover', 'back'].map((role) => (
                  <Checkbox
                    key={role}
                    label={`Exclude ${role} from numbering`}
                    checked={(booklet.page_numbers?.exclude_roles || []).includes(role)}
                    disabled={booklet.page_numbers?.enabled === false}
                    onChange={(checked) => {
                      const currentRoles = booklet.page_numbers?.exclude_roles || [];
                      const nextRoles = checked
                        ? [...new Set([...currentRoles, role])]
                        : currentRoles.filter((currentRole) => currentRole !== role);
                      update(
                        ['outputs', 'booklet', 'page_numbers', 'exclude_roles'],
                        nextRoles
                      );
                    }}
                  />
                ))}
              </InlineGrid>
            </Section>
          )}

          {cover && (
            <Section title="Cover sheet">
              <Select
                label="Layout"
                options={storyType === 'dynamic_story'
                  ? [
                    { label: 'Dynamic background (2-scene front + back)', value: 'dynamic_background' },
                    { label: 'Wrap / Single full-bleed cover', value: 'wrap' },
                  ]
                  : [{ label: 'A3 background', value: 'a3_background' }]}
                value={cover.variant}
                onChange={(value) => update(['outputs', 'cover', 'variant'], value)}
                helpText="Choose 'Wrap / Single full-bleed cover' for single-scene covers (e.g. coloring books)."
              />
              <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
                <NumberField
                  label="Sheet width"
                  value={cover.width_px}
                  suffix="px"
                  onChange={(value) => update(['outputs', 'cover', 'width_px'], value)}
                />
                <NumberField
                  label="Sheet height"
                  value={cover.height_px}
                  suffix="px"
                  onChange={(value) => update(['outputs', 'cover', 'height_px'], value)}
                />
                <NumberField
                  label="Resolution"
                  value={cover.dpi}
                  suffix="DPI"
                  onChange={(value) => update(['outputs', 'cover', 'dpi'], value)}
                />
              </InlineGrid>
              {cover.branding && (
                <BlockStack gap="300">
                  <Checkbox
                    label="Cover gradient + logo branding"
                    checked={cover.branding.enabled === true}
                    onChange={(checked) => update(
                      ['outputs', 'cover', 'branding', 'enabled'],
                      checked
                    )}
                    helpText="Adds border gradients and the Stories by Snaps logo on the cover output."
                  />
                  {cover.branding.enabled === true && (
                    <BlockStack gap="300">
                      <Text variant="headingSm" as="h4">Gradient edges</Text>
                      <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
                        <TextField
                          label="Gradient color"
                          value={cover.branding.gradient?.color || cover.branding.gradient_color || '#ffffff'}
                          onChange={(value) => {
                            update(['outputs', 'cover', 'branding', 'gradient_color'], value);
                            update(['outputs', 'cover', 'branding', 'gradient', 'color'], value);
                          }}
                        />
                        <NumberField
                          label="Peak opacity"
                          value={cover.branding.gradient?.opacity ?? cover.branding.gradient_bottom_opacity ?? 1.0}
                          step={0.01}
                          min={0}
                          max={1}
                          onChange={(value) => {
                            update(['outputs', 'cover', 'branding', 'gradient_bottom_opacity'], value);
                            update(['outputs', 'cover', 'branding', 'gradient', 'opacity'], value);
                          }}
                        />
                      </InlineGrid>
                      <InlineGrid columns={{ xs: 2, sm: 4 }} gap="300">
                        <NumberField
                          label="Top gradient"
                          value={cover.branding.gradient?.top_cm ?? 0}
                          suffix="cm"
                          step={0.1}
                          min={0}
                          onChange={(value) => update(
                            ['outputs', 'cover', 'branding', 'gradient', 'top_cm'],
                            value
                          )}
                        />
                        <NumberField
                          label="Right gradient"
                          value={cover.branding.gradient?.right_cm ?? 0}
                          suffix="cm"
                          step={0.1}
                          min={0}
                          onChange={(value) => update(
                            ['outputs', 'cover', 'branding', 'gradient', 'right_cm'],
                            value
                          )}
                        />
                        <NumberField
                          label="Bottom gradient"
                          value={cover.branding.gradient?.bottom_cm ?? 0}
                          suffix="cm"
                          step={0.1}
                          min={0}
                          onChange={(value) => update(
                            ['outputs', 'cover', 'branding', 'gradient', 'bottom_cm'],
                            value
                          )}
                        />
                        <NumberField
                          label="Left gradient"
                          value={cover.branding.gradient?.left_cm ?? 0}
                          suffix="cm"
                          step={0.1}
                          min={0}
                          onChange={(value) => update(
                            ['outputs', 'cover', 'branding', 'gradient', 'left_cm'],
                            value
                          )}
                        />
                      </InlineGrid>

                      <Divider />

                      <Text variant="headingSm" as="h4">Logo positioning & sizing</Text>
                      <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
                        <Select
                          label="Logo color"
                          options={LOGO_ASSET_OPTIONS}
                          value={cover.branding.logo?.asset || 'snaps-stories-logo-white.png'}
                          onChange={(value) => update(
                            ['outputs', 'cover', 'branding', 'logo', 'asset'],
                            value
                          )}
                        />
                        <Select
                          label="Logo position"
                          options={LOGO_POSITION_OPTIONS}
                          value={cover.branding.logo?.position || 'bottom_center'}
                          onChange={(value) => update(
                            ['outputs', 'cover', 'branding', 'logo', 'position'],
                            value
                          )}
                        />
                        <NumberField
                          label="Logo width ratio"
                          value={cover.branding.logo?.width_pct ?? cover.branding.logo_width_pct ?? 0.28}
                          suffix="× page"
                          step={0.01}
                          onChange={(value) => {
                            update(['outputs', 'cover', 'branding', 'logo_width_pct'], value);
                            update(['outputs', 'cover', 'branding', 'logo', 'width_pct'], value);
                          }}
                        />
                        <NumberField
                          label="Logo width (cm)"
                          value={cover.branding.logo?.width_cm ?? 0}
                          suffix="cm"
                          step={0.1}
                          min={0}
                          helpText="Overrides width ratio if > 0"
                          onChange={(value) => update(
                            ['outputs', 'cover', 'branding', 'logo', 'width_cm'],
                            value
                          )}
                        />
                        <NumberField
                          label="Bottom / top margin"
                          value={cover.branding.logo?.bottom_margin_cm ?? 0}
                          suffix="cm"
                          step={0.1}
                          min={0}
                          onChange={(value) => update(
                            ['outputs', 'cover', 'branding', 'logo', 'bottom_margin_cm'],
                            value
                          )}
                        />
                        <NumberField
                          label="Horizontal margin"
                          value={cover.branding.logo?.horizontal_margin_cm ?? 0}
                          suffix="cm"
                          step={0.1}
                          min={0}
                          onChange={(value) => update(
                            ['outputs', 'cover', 'branding', 'logo', 'horizontal_margin_cm'],
                            value
                          )}
                        />
                      </InlineGrid>
                    </BlockStack>
                  )}
                </BlockStack>
              )}
              {cover.variant === 'wrap' && (
                <BlockStack gap="300">
                  <Checkbox
                    label="Separate back cover page"
                    checked={cover.separate_back_page === true}
                    onChange={(checked) => {
                      update(['outputs', 'cover', 'separate_back_page'], checked);
                      if (checked && !cover.back_branding) {
                        update(['outputs', 'cover', 'back_branding'], defaultBackBranding());
                      }
                    }}
                    helpText="Adds the back scene as page 2 of the cover PDF. The QR layer below is drawn on that page instead of on the front cover."
                  />
                  {cover.separate_back_page === true && cover.back_branding && (
                    <BlockStack gap="300">
                      <Checkbox
                        label="Back cover gradient (no logo)"
                        checked={cover.back_branding.enabled === true}
                        onChange={(checked) => update(
                          ['outputs', 'cover', 'back_branding', 'enabled'],
                          checked
                        )}
                      />
                      {cover.back_branding.enabled === true && (
                        <BlockStack gap="300">
                          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                            <TextField
                              label="Back gradient color"
                              value={cover.back_branding.gradient?.color || '#ffffff'}
                              autoComplete="off"
                              onChange={(value) => update(
                                ['outputs', 'cover', 'back_branding', 'gradient', 'color'],
                                value
                              )}
                            />
                            <NumberField
                              label="Back peak opacity"
                              value={cover.back_branding.gradient?.opacity ?? 1}
                              step={0.01}
                              min={0}
                              max={1}
                              onChange={(value) => update(
                                ['outputs', 'cover', 'back_branding', 'gradient', 'opacity'],
                                value
                              )}
                            />
                          </InlineGrid>
                          <InlineGrid columns={{ xs: 2, sm: 4 }} gap="300">
                            {[
                              ['Top gradient', 'top_cm'],
                              ['Right gradient', 'right_cm'],
                              ['Bottom gradient', 'bottom_cm'],
                              ['Left gradient', 'left_cm'],
                            ].map(([label, key]) => (
                              <NumberField
                                key={key}
                                label={label}
                                value={cover.back_branding.gradient?.[key] ?? 0}
                                suffix="cm"
                                step={0.1}
                                min={0}
                                onChange={(value) => update(
                                  ['outputs', 'cover', 'back_branding', 'gradient', key],
                                  value
                                )}
                              />
                            ))}
                          </InlineGrid>
                        </BlockStack>
                      )}
                    </BlockStack>
                  )}
                </BlockStack>
              )}
              {cover.back_layer && (
                <BlockStack gap="300">
                  <Checkbox
                    label="Back cover QR layer"
                    checked={cover.back_layer.enabled === true}
                    onChange={(checked) => update(
                      ['outputs', 'cover', 'back_layer', 'enabled'],
                      checked
                    )}
                    helpText="Centers the static QR card on the back cover. Used by the Hard Cover sheet (left half for LTR, right for RTL), the Booklet back page, and the separate back cover page when enabled."
                  />
                  {cover.back_layer.enabled === true && (
                    <BlockStack gap="300">
                      <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                        <Select
                          label="Layer image"
                          options={BACK_LAYER_ASSET_OPTIONS}
                          value={cover.back_layer.asset}
                          onChange={(value) => update(
                            ['outputs', 'cover', 'back_layer', 'asset'],
                            value
                          )}
                        />
                        <NumberField
                          label="Layer width"
                          value={cover.back_layer.width_cm}
                          suffix="cm"
                          step={0.1}
                          min={0}
                          helpText="Image is 10.1 cm at native 300 dpi; height follows the image ratio."
                          onChange={(value) => update(
                            ['outputs', 'cover', 'back_layer', 'width_cm'],
                            value
                          )}
                        />
                      </InlineGrid>
                      <Checkbox
                        label="Print Order ID beside the QR code"
                        checked={cover.back_layer.order_id?.enabled === true}
                        onChange={(checked) => update(
                          ['outputs', 'cover', 'back_layer', 'order_id', 'enabled'],
                          checked
                        )}
                        helpText="Vertical text, reading bottom to top. The # prefix is removed."
                      />
                      {cover.back_layer.order_id?.enabled === true && (
                        <InlineGrid columns={{ xs: 2, sm: 4 }} gap="300">
                          <NumberField
                            label="Position X"
                            value={cover.back_layer.order_id.x_pct}
                            suffix="× layer"
                            step={0.01}
                            min={0}
                            max={1}
                            helpText="0 = left edge, 1 = right edge"
                            onChange={(value) => update(
                              ['outputs', 'cover', 'back_layer', 'order_id', 'x_pct'],
                              value
                            )}
                          />
                          <NumberField
                            label="Position Y"
                            value={cover.back_layer.order_id.y_pct}
                            suffix="× layer"
                            step={0.01}
                            min={0}
                            max={1}
                            helpText="0 = top edge, 1 = bottom edge"
                            onChange={(value) => update(
                              ['outputs', 'cover', 'back_layer', 'order_id', 'y_pct'],
                              value
                            )}
                          />
                          <NumberField
                            label="Font size"
                            value={cover.back_layer.order_id.font_size_pt}
                            suffix="pt"
                            step={0.5}
                            min={1}
                            onChange={(value) => update(
                              ['outputs', 'cover', 'back_layer', 'order_id', 'font_size_pt'],
                              value
                            )}
                          />
                          <TextField
                            label="Text color"
                            value={cover.back_layer.order_id.color}
                            onChange={(value) => update(
                              ['outputs', 'cover', 'back_layer', 'order_id', 'color'],
                              value
                            )}
                            autoComplete="off"
                          />
                        </InlineGrid>
                      )}
                    </BlockStack>
                  )}
                </BlockStack>
              )}
              {cover.variant === 'dynamic_background' && (
                <BlockStack gap="300">
                <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="300">
                  <NumberField
                    label="Cover width"
                    value={cover.cover_width_px}
                    suffix="px"
                    onChange={(value) => update(
                      ['outputs', 'cover', 'cover_width_px'],
                      value
                    )}
                  />
                  <NumberField
                    label="Cover height"
                    value={cover.cover_height_px}
                    suffix="px"
                    onChange={(value) => update(
                      ['outputs', 'cover', 'cover_height_px'],
                      value
                    )}
                  />
                  <NumberField
                    label="Half spine gap"
                    value={cover.inner_gap_half_px}
                    suffix="px"
                    onChange={(value) => update(
                      ['outputs', 'cover', 'inner_gap_half_px'],
                      value
                    )}
                  />
                </InlineGrid>
                <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                  <NumberField
                    label="Cover width"
                    value={cover.cover_width_cm}
                    suffix="cm"
                    onChange={(value) => update(
                      ['outputs', 'cover', 'cover_width_cm'],
                      value
                    )}
                  />
                  <NumberField
                    label="Cover height"
                    value={cover.cover_height_cm}
                    suffix="cm"
                    onChange={(value) => update(
                      ['outputs', 'cover', 'cover_height_cm'],
                      value
                    )}
                  />
                  <NumberField
                    label="Outer margin"
                    value={cover.outer_margin_cm}
                    suffix="cm"
                    onChange={(value) => update(
                      ['outputs', 'cover', 'outer_margin_cm'],
                      value
                    )}
                  />
                  <NumberField
                    label="Inner gap"
                    value={cover.inner_gap_cm}
                    suffix="cm"
                    onChange={(value) => update(
                      ['outputs', 'cover', 'inner_gap_cm'],
                      value
                    )}
                  />
                </InlineGrid>
                </BlockStack>
              )}
            </Section>
          )}

          {readable && (
            <Section title="Readable PDF sizes">
              {Object.entries(readable.variants || {}).map(([variantKey, variant]) => (
                <div key={variantKey}>
                  <BlockStack gap="200">
                    <Text variant="headingSm" as="h4">{variantKey}</Text>
                    <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                      <NumberField
                        label="Width"
                        value={variant.width_cm}
                        suffix="cm"
                        onChange={(value) => update(
                          ['outputs', 'readable', 'variants', variantKey, 'width_cm'],
                          value
                        )}
                      />
                      <NumberField
                        label="Height"
                        value={variant.height_cm}
                        suffix="cm"
                        onChange={(value) => update(
                          ['outputs', 'readable', 'variants', variantKey, 'height_cm'],
                          value
                        )}
                      />
                      <NumberField
                        label="Horizontal number margin"
                        value={variant.page_numbers?.horizontal_margin_cm}
                        suffix="cm"
                        onChange={(value) => update(
                          [
                            'outputs',
                            'readable',
                            'variants',
                            variantKey,
                            'page_numbers',
                            'horizontal_margin_cm',
                          ],
                          value
                        )}
                      />
                      <NumberField
                        label="Bottom number margin"
                        value={variant.page_numbers?.bottom_margin_cm}
                        suffix="cm"
                        onChange={(value) => update(
                          [
                            'outputs',
                            'readable',
                            'variants',
                            variantKey,
                            'page_numbers',
                            'bottom_margin_cm',
                          ],
                          value
                        )}
                      />
                    </InlineGrid>
                    <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                      <NumberField
                        label="Scene width"
                        value={variant.scene_width_cm}
                        suffix="cm"
                        onChange={(value) => update(
                          ['outputs', 'readable', 'variants', variantKey, 'scene_width_cm'],
                          value
                        )}
                      />
                      <NumberField
                        label="Scene height"
                        value={variant.scene_height_cm}
                        suffix="cm"
                        onChange={(value) => update(
                          ['outputs', 'readable', 'variants', variantKey, 'scene_height_cm'],
                          value
                        )}
                      />
                      <NumberField
                        label="Outer scene margin"
                        value={variant.outer_margin_cm}
                        suffix="cm"
                        onChange={(value) => update(
                          ['outputs', 'readable', 'variants', variantKey, 'outer_margin_cm'],
                          value
                        )}
                      />
                      <NumberField
                        label="Top scene margin"
                        value={variant.top_margin_cm}
                        suffix="cm"
                        onChange={(value) => update(
                          ['outputs', 'readable', 'variants', variantKey, 'top_margin_cm'],
                          value
                        )}
                      />
                    </InlineGrid>
                  </BlockStack>
                  <div style={{ margin: '16px 0' }}><Divider /></div>
                </div>
              ))}
            </Section>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export {
  buildProfilePayload,
  emptyProfile,
  normalizeProfileForForm,
};

export default MontageProfileModal;
